<template>
  <span class="text-left">
    <a class="row-action" href="javascript:;" @click="showModal(showAddReceipt)">
      <i class="fas fa-camera" :style="{ color: color }"></i>
    </a>
    <ed-modal title="Upload Receipt"
              :show="showAddReceipt"
              @close="showModal(showAddReceipt), changeIconColor(), goBackToInitialValue()"
              @confirm="handleConfirm"
              :primary-button-disabled="(!uploadFinished || disabled) && !isUserAdmin"
              primary-button="Submit">
      <div slot="body" class='add-receipt-overlay'>
        <span style="font-size: 12px">Accepted formats: pdf, png, jpg, jpeg</span>
        <div>
          <ed-upload button-label='Upload New Receipt'
                     :class='{loading: !uploadFinished}'
                     v-model='uploaderFiles'
                     v-tooltip="!(isUserAdmin || isDraft) && disabled ? permissionTooltip : ''"
                     readonly
                     :multiple='true'
                     :postAction='api.env + api.$.evedpaycard.receipt'
                     :uploadAuto='true'
                     :canOpenFile='true'
                     @input='createAttachments'
                     :headers='{Authorization: "Bearer " + token}'
                     :disabled="!(isUserAdmin || isDraft) && disabled"
                     name='attachment_upload'
                     accept='application/pdf, image/png, image/jpeg'
                     extensions='pdf,png,jpg,jpeg'>
          </ed-upload>
        </div>
      </div>
    </ed-modal>
  </span>
</template>

<script>
  export default {
    data() {
      return {
        showAddReceipt: false,
        uploaderFiles: [],
        attachedPath: [],
        token: this.$user.sessionSettings.Token,
        color: '#000000',
        initialAttachedFiles: [],
        submitPressed: false,
        uploadFinished: true
      }
    },

    props: {
      rowData: Object,
      incomplete: false,
      attachments: Object,
      disabled: false,
      isDraft: false
    },

    async created() {
      try {
        this.uploaderFiles = this.mapUploaderFiles(this.attachments)
        this.changeIconColor()
      } catch (e) {
        console.error('Error fetching transaction files:', e)
      }
    },

    watch: {
      incomplete() {
        this.changeIconColor()
      },
      attachments(newValues) {
        this.uploaderFiles = newValues ? newValues.map(file => this.mapFile(file)) : {}
        this.changeIconColor()
      }
    },
    computed: {
      isUserAdmin() {
        if (this.$user.$.userPermissions.includes('EvedPayCardAdministrator')) {
          return true
        }
        return false
      },
      permissionTooltip() {
        return 'You do not have permission to change receipt attachments on this transaction.'
      }
    },
    methods: {
      showModal(showAddReceipt) {
        this.showAddReceipt = !showAddReceipt
        if (this.showAddReceipt) {
          this.initialAttachedFiles = this.deepCopy(this.uploaderFiles)
          this.submitPressed = false
          this.uploadFinished = true
        }
      },

      goBackToInitialValue() {
        if (!this.submitPressed) {
          this.uploaderFiles = this.initialAttachedFiles
        }
        this.changeIconColor()
      },

      changeIconColor() {
        this.incomplete = this.incomplete && ((this.uploaderFiles && this.uploaderFiles.length === 0) || !this.uploaderFiles)
        this.color = this.incomplete ? '#FF0000' : (this.uploaderFiles && this.uploaderFiles.length ? '#4A90E2' : '#000000')
      },

      mapUploaderFiles(files) {
        files = files || []

        let result = []
        files.forEach((file) => {
          result.push(this.mapFile(file))
        })
        return result
      },

      mapFile(file) {
        var parts = file.OriginalName.split('.')
        var extension = ''
        var fileType = ''
        if (parts.length > 1) {
          extension = parts[parts.length - 1].toLowerCase()
        }
        switch (extension) {
          case 'png':
            fileType = 'image/png'
            break
          case 'pdf':
            fileType = 'application/pdf'
            break
          case 'jpg':
            fileType = 'image/jpg'
            break
          case 'jpeg':
            fileType = 'image/jpeg'
            break
          default:
            fileType = ''
        }
        return {
          name: file.OriginalName,
          type: fileType,
          size: file.SizeBytes,
          file: file.MediaFile,
          mediaFile: file.MediaFile,
          path: file.FullPath,
          pathWithName: file.PathWithName,
          id: file.Id,
          success: true,
          readonly: this.disabled && !this.isUserAdmin
        }
      },

      createAttachments(e) {
        e.forEach(x => {
          var parts = x.name.split('.')
          var extension = ''
          if (parts.length > 1) {
            extension = parts[parts.length - 1]
          }
          if (!['pdf', 'png', 'jpg', 'jpeg'].includes(extension.toLowerCase())) {
            this.uploaderFiles = this.uploaderFiles.filter(x => x.success)
            this.showWarningMessage(`The file "${x.name}" is not compatible. Please upload files with one of the following extensions: .pdf, .png, .jpg, .jpeg`)
            return
          }

          const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
          if (x.size >= MAX_FILE_SIZE) {
            this.uploaderFiles = this.uploaderFiles.filter(x => x.size <= MAX_FILE_SIZE)
            this.showWarningMessage(`The file "${x.name}" is too large. Please upload files smaller than 10 MB.`)
            return
          }
        })
        let completedFiles = e.filter(
          item => item.success || // files uploaded with success
            (!item.success && item.response && typeof item.response === 'string' && !this.isJson(item.response)) // files where requested failed
        )

        if (completedFiles.length === e.length) {
          this.uploadFinished = true

          let uploadedFiles = e.map((item) => {
            let uploadError = ''
            let itemResponse = null
            let clientDataPath = ''
            let mediaFile = ''

            if (item.success && item.response) {
              itemResponse = JSON.parse(item.response)
              if (itemResponse.success) {
                const regex = /(ClientData\/.*)/
                const match = itemResponse.path.match(regex)
                if (match && match.length > 1) {
                  const filePath = match[1]
                  clientDataPath = filePath.substring(filePath.indexOf('ClientData'))
                  this.attachedPath.push(clientDataPath)
                  const splits = clientDataPath.split('/')
                  mediaFile = splits[splits.length - 1]
                } else {
                  console.log('No matching files.')
                }
              }
              uploadError = (itemResponse && itemResponse.uploadError) ? itemResponse.uploadError : ''
            }
            if (!item.success) {
              uploadError = 'Upload request failed'
            }

            const vendorId = this.$root.getUser.$.data.VendorID
            const VCI = this.$root.getUser.$.data.VCI

            if (uploadError) {
              this.showWarningMessage('File "' + item.name + '": ' + uploadError)
            } else {
              return {
                OriginalName: item.name,
                SizeBytes: item.size,
                MediaFile: item.mediaFile ? item.mediaFile : mediaFile || '',
                AttachmentFile: itemResponse ? itemResponse.file : (item.file || ''),
                Id: item.id ? item.id : '',
                PathWithName: item.pathWithName ? item.pathWithName : itemResponse ? clientDataPath : '',
                ReadOnly: item.readonly || false,
                VendorId: vendorId,
                FullPath: item.path ? item.path : itemResponse.path ? itemResponse.path : '',
                VCI: VCI
              }
            }
          })

          this.mediaFilesPath = this.attachedPath.join(', ')
          this.$emit('mediaFilesPathUpdated', this.mediaFilesPath)
          this.attachedPath = []

          try {
            this.uploaderFiles = this.uploaderFiles.filter(item =>
              item.success &&
              (
                (!item.response && item.name) || // take existing items from edit
                (JSON.parse(item.response).success) // take new added items
              )
            ).map(item => {
              if (item.response) {
                const response = JSON.parse(item.response)
                if (response.path) {
                  item.path = response.path // assign the 'path' property from the response to the item
                }
              }
              return item
            })
          } catch (ee) {
            console.error(ee)
          }
          this.newFiles = uploadedFiles.filter(item => item)
          this.$forceUpdate()
        } else {
          this.uploadFinished = false
        }
      },

      deepCopy(obj) {
        if (obj === null || typeof obj !== 'object') {
          return obj
        }

        if (Array.isArray(obj)) {
          return obj.map(element => this.deepCopy(element))
        }

        const newObj = {}
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            newObj[key] = this.deepCopy(obj[key])
          }
        }

        return newObj
      },

      async submitFiles() {
        this.submitPressed = true
        this.$emit('attachmentsUpdated', this.newFiles)

        if (this.disabled && this.isUserAdmin) {
          const payload = {
            transactionId: this.rowData.Id,
            receiptFiles: this.newFiles.map(f => ({
              id: f.Id || '00000000-0000-0000-0000-000000000000',
              pathWithName: f.PathWithName,
              originalName: f.OriginalName,
              mediaFile: f.MediaFile,
              sizeBytes: f.SizeBytes,
              vendorId: f.VendorId
            }))
          }

          try {
            await this.api.post(
              this.api.$.evedpaycard.updateReceipt,
              payload
            )
            this.$notifications.notify({
              type: 'info',
              message: 'Receipt updated successfully',
              placement: { from: 'top', align: 'center' }
            })
          } catch (e) {
            console.error('Error while updating the receipt:', e)
          }
        }
      },
      async handleConfirm() {
        this.changeIconColor()

        if (this.rowData.EnvelopeId && (!this.newFiles || this.newFiles.length === 0) && !this.isDraft) {
          this.$notifications.notify({
            type: 'info-warn',
            message: 'Receipt is required. Please upload at least one file.',
            placement: { from: 'top', align: 'center' }
          })
          this.showAddReceipt = false
          return
        }

        await this.submitFiles()
      },
      isJson(str) {
        try {
          JSON.parse(str)
        } catch (e) {
          return false
        }
        return true
      },

      showWarningMessage(message) {
        this.$nextTick(() => {
          this.$notifications.notify({
            type: 'plain',
            icon: 'exclamation-circle',
            placement: { from: 'top', align: 'center' },
            timeout: 0,
            message: message,
            discard: 'Ok',
            goto: ''
          })
        })
      }
    }
  }
</script>

<style lang='scss' scoped>
  .add-receipt-overlay {
    background-color: white;
    padding: 20px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }
</style>



// WEBPACK FOOTER //
// src/components/_modules/Programs/Card-transactions/AddReceipt.vue